// Pure helpers for the /canchas map view. No leaflet import here — this
// module stays synchronous and unit-testable without mocking react-leaflet.

// San Miguel de Tucumán — reference market center used when no club has
// valid coordinates (empty filter results, or all-null-coord clubs).
export const DEFAULT_CENTER = { lat: -26.8241, lng: -65.2226 };
export const DEFAULT_ZOOM = 12;

// Every useJsApiLoader call sharing the "google-map-script" id must pass the
// exact same options, or the Google loader throws at runtime. This array is
// module-level so its reference stays stable across renders too — an inline
// literal would make the loader think the options changed on every render.
export const GOOGLE_MAPS_LIBRARIES = ["places"];

// Country used to disambiguate address lookups (geocoding and autocomplete).
export const GEOCODING_COUNTRY = "AR";

function isFiniteCoord(value) {
	return typeof value === "number" && Number.isFinite(value);
}

/**
 * Maps clubs to the minimal marker shape consumed by CanchasMapView,
 * dropping any club without valid (non-null, finite) lat/lng.
 */
export function toMapPoints(clubs) {
	if (!Array.isArray(clubs)) return [];

	return clubs
		.filter((club) => {
			if (!club) return false;
			const hasCoords =
				club.lat != null &&
				club.lng != null &&
				isFiniteCoord(club.lat) &&
				isFiniteCoord(club.lng);
			const hasAddress = Boolean(club.address && club.address.trim());
			return hasCoords || hasAddress;
		})
		.map((club) => {
			const cityName = club.cityid?.name || club.city || "";
			const stateName = club.stateid?.name || club.state || "";
			const fullAddress = [club.address, cityName, stateName]
				.filter(Boolean)
				.join(", ");

			return {
				id: club._id,
				slugOrId: club.slug || club._id,
				name: club.name,
				lat: isFiniteCoord(club.lat) ? club.lat : null,
				lng: isFiniteCoord(club.lng) ? club.lng : null,
				address: fullAddress || club.address,
				image: club.image,
			};
		});
}

/**
 * Builds the `q` value for a Google Maps embed of a club.
 *
 * Confirmed coordinates win over the address text: they are the precise,
 * human-placed pin, while the address is only as good as whatever the
 * geocoder makes of it. Falls back to "address, city, state" when the club
 * has no stored coordinates. Returns an unencoded string (or null when
 * there is nothing to show) — callers encode it.
 */
export function buildLocationMapQuery(location) {
	if (!location) return null;

	const lat = Number(location.lat);
	const lng = Number(location.lng);
	if (
		location.lat != null &&
		location.lng != null &&
		isFiniteCoord(lat) &&
		isFiniteCoord(lng)
	) {
		return `${lat},${lng}`;
	}

	const cityName = location.cityid?.name || location.city || "";
	const stateName = location.stateid?.name || location.state || "";
	const query = [location.address, cityName, stateName]
		.filter(Boolean)
		.join(", ");

	return query || null;
}

// Backwards-compatible club name retained for existing consumers.
export function buildMapQuery(club) {
	return buildLocationMapQuery(club);
}

/**
 * Returns the centroid of the given points, or DEFAULT_CENTER when the
 * list is empty (keeps the map at a sensible city view with zero markers).
 */
export function computeCenter(points) {
	const coordinatePoints = Array.isArray(points)
		? points.filter(
				(point) => isFiniteCoord(point?.lat) && isFiniteCoord(point?.lng),
			)
		: [];

	if (coordinatePoints.length === 0) {
		return DEFAULT_CENTER;
	}

	const total = coordinatePoints.reduce(
		(acc, point) => ({
			lat: acc.lat + point.lat,
			lng: acc.lng + point.lng,
		}),
		{ lat: 0, lng: 0 },
	);

	return {
		lat: total.lat / coordinatePoints.length,
		lng: total.lng / coordinatePoints.length,
	};
}
